import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

import { CopyButton } from "@components";

import TruncatedTextWithTooltip from "../../components/TruncatedTextWithTooltip";
import { COPY_ICON_SPACING } from "../../helpers/constants";
import type { FieldRenderer } from "../types";

const Wrapper = styled("div")({
  position: "relative",
  "&:hover .copyTextButton": {
    opacity: 1,
  },
});

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const Item = styled("div")(() => ({
  lineHeight: 1.4,
  overflow: "hidden",
}));

const MoreText = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontStyle: "italic",
  lineHeight: 1.4,
}));

const StyledCopyButton = styled(CopyButton)({
  position: "absolute",
  right: `-${COPY_ICON_SPACING}`,
  top: "50%",
  transform: "translateY(-50%)",
});

type StringListRendererProps = {
  items: string[];
  maxVisible?: number;
};

const StringListRendererComponent = React.memo(function StringListRenderer({
  items,
  maxVisible,
}: StringListRendererProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const visible = maxVisible ? items.slice(0, maxVisible) : items;
  const remaining = maxVisible ? items.length - maxVisible : 0;
  const copyText = items.join(", ");

  return (
    <Wrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Container>
        {visible.map((item, index) => (
          <Item key={index}>
            <TruncatedTextWithTooltip text={item} placement="bottom-start" />
          </Item>
        ))}
        {remaining > 0 && <MoreText>+{remaining} more</MoreText>}
      </Container>
      <Box
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        className="copyTextButton"
      >
        <StyledCopyButton
          text={copyText}
          size="small"
          visibleOnHover={true}
          isHovered={isHovered}
          tooltipText="Copy all to clipboard"
        />
      </Box>
    </Wrapper>
  );
});

// Truncated version (default export) - shows max 3 items
const TruncatedStringList: FieldRenderer = (fieldValue: unknown) => {
  const items = Array.isArray(fieldValue) ? fieldValue : [];
  const formattedText = items.join(", ");

  return {
    formattedText,
    node: <StringListRendererComponent items={items} maxVisible={3} />,
  };
};

// Non-truncated version (named export) - shows all items
export const StringList: FieldRenderer = (fieldValue: unknown) => {
  const items = Array.isArray(fieldValue) ? fieldValue : [];
  const formattedText = items.join(", ");

  return {
    formattedText,
    node: <StringListRendererComponent items={items} />,
  };
};

export default TruncatedStringList;
